"use client"

import "../../CSS/Candidate.css"
import { useEffect, useState, useMemo } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { fetchCandidateListAsync, selectCandidates } from "./CandidateSlice/Basic/CandidateSlice"
import { Tabs, TabList, Tab, Box, Flex, Badge, Text, Tooltip, Button, HStack, Spacer } from "@chakra-ui/react"
import noData from "../nodata1.png"

export default function Candidate() {
  const dispatch = useDispatch()
  const candidates = useSelector(selectCandidates)
  const location = useLocation()
  const navigate = useNavigate()

  const totalApplicants = candidates?.total_applicant_count || 0
  const records = candidates?.records?.data
  const counts = candidates?.status_counts || {}

  const getTabIndex = () => {
    if (location.pathname.includes("/shortlisted")) return 1
    if (location.pathname.includes("/scheduled")) return 2
    if (location.pathname.includes("/rejected")) return 3
    if (location.pathname.includes("/hired")) return 4
    if (location.pathname.includes("/awaiting-review")) return 5
    if (location.pathname.includes("/contacting")) return 6
    return 0
  }

  useEffect(() => {
    let status = ""
    if (location.pathname.includes("/shortlisted")) status = "shortlist"
    else if (location.pathname.includes("/scheduled")) status = "interview_scheduled"
    else if (location.pathname.includes("/rejected")) status = "rejected"
    else if (location.pathname.includes("/hired")) status = "hired"
    else if (location.pathname.includes("/awaiting-review")) status = "on_hold"
    else if (location.pathname.includes("/contacting")) status = "contacting"

    dispatch(fetchCandidateListAsync({ page: 1, status }))
    window.scrollTo(0, 0)
  }, [dispatch, location.pathname])

  const handleTabChange = (index) => {
    switch (index) {
      case 0: navigate("/employer-dashboard/candidate"); break
      case 1: navigate("/employer-dashboard/candidate/shortlisted"); break
      case 2: navigate("/employer-dashboard/candidate/scheduled"); break
      case 3: navigate("/employer-dashboard/candidate/rejected"); break
      case 4: navigate("/employer-dashboard/candidate/hired"); break
      case 5: navigate("/employer-dashboard/candidate/awaiting-review"); break
      case 6: navigate("/employer-dashboard/candidate/contacting"); break
      default: navigate("/employer-dashboard/candidate")
    }
  }

  return (
    <div className="">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          <Flex justify="space-between" align="center" wrap="wrap" mb={3} gap={2}>
            <Box>
              <h1 className="sm:text-4xl text-xl font-semibold text-left">Candidates</h1>
              <Text fontSize="sm" color="gray.600">Manage applicants by stage and review profiles</Text>
            </Box>
            <HStack spacing={3}>
              <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                Total Applicants: {totalApplicants}
              </Badge>
            </HStack>
          </Flex>

          <div className="w-full bg-white rounded-xl">
            <Box px={4} py={4}>
              <Tabs
                index={getTabIndex()}
                onChange={handleTabChange}
                variant="enclosed"
                colorScheme="blue"
                isFitted
              >
                <TabList display="flex" flexWrap="wrap">
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>All Applicants</Box>
                      <Badge colorScheme="blue" variant="subtle">{totalApplicants}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>Shortlisted</Box>
                      <Badge colorScheme="green" variant="subtle">{counts?.shortlist || 0}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>Interview</Box>
                      <Badge colorScheme="orange" variant="subtle">{counts?.interview_scheduled || 0}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>Rejected</Box>
                      <Badge colorScheme="red" variant="subtle">{counts?.rejected || 0}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>Hired</Box>
                      <Badge colorScheme="purple" variant="subtle">{counts?.hired || 0}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>On Hold</Box>
                      <Badge colorScheme="yellow" variant="subtle">{counts?.on_hold || 0}</Badge>
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: "blue.600", borderColor: "blue.600" }} fontSize="sm">
                    <Flex alignItems="center" gap={2}>
                      <Box>Contacting</Box>
                      <Badge colorScheme="cyan" variant="subtle">{counts?.contacted || 0}</Badge>
                    </Flex>
                  </Tab>
                </TabList>
              </Tabs>
            </Box>

            <div className="">
              {records && Array.isArray(records) && records.length > 0 ? (
                <div className="px-4">
                  <Outlet />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center w-full p-6">
                  <img src={noData || "/placeholder.svg"} alt="No candidates" className="object-contain h-[280px] w-[280px]" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-1 mt-2">No Applicant Found</h3>
                  <p className="text-gray-600 text-center max-w-md">
                    We couldn't find any applicants in this stage at the moment. Please check back later.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}